import { getApiConfigNext } from '@y-sports/api-client';
import { TeamDataService, type ManagedTeamOption } from './TeamDataService';
import type { ResourcePermissions } from './AccessDataService';

export type LeagueAccessMode = 'OPEN' | 'COUNTRY_RESTRICTED' | 'INVITATIONAL';
export type LeagueParticipationType = 'TEAM' | 'INDIVIDUAL';
export type LeagueScheduleType = 'ONE_TIME' | 'WEEKLY';
export type LeagueMatchStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'FINISHED' | 'CANCELLED';
export type LeagueEventStatus =
  | 'DRAFT'
  | 'REGISTRATION_OPEN'
  | 'REGISTRATION_CLOSED'
  | 'TEAMS_GENERATED'
  | 'CHECK_IN_OPEN'
  | 'IN_PROGRESS'
  | 'VOTING_OPEN'
  | 'CLOSED'
  | 'CANCELLED';
export type LeagueEventApplicationStatus =
  | 'SUBMITTED'
  | 'WAITLISTED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'WITHDRAWN'
  | 'LATE_PENDING'
  | 'CHECKED_IN'
  | 'NO_SHOW';
export type LeagueEventMatchScoringMode = 'LIGHTWEIGHT' | 'PROTOCOL';
export type QrTargetType =
  | 'USER_PROFILE'
  | 'PLAYER_PROFILE'
  | 'PLAYER_CLAIM'
  | 'LEAGUE_JOIN'
  | 'LEAGUE_EVENT_REGISTRATION'
  | 'LEAGUE_EVENT_CHECK_IN'
  | 'LEAGUE_ROLE_INVITE'
  | 'TOURNAMENT_REGISTRATION'
  | 'MATCH_ACCESS';
export type QrActionType = 'VIEW' | 'CLAIM_PLAYER' | 'REGISTER' | 'CHECK_IN' | 'MATCH_ACCESS' | 'ACCEPT_ROLE';
export type LeagueRole = 'LEAGUE_ADMIN' | 'EVENT_MANAGER' | 'CHECK_IN_OPERATOR' | 'SCORER' | 'VIEWER';
export type LeagueRoleStatus = 'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'REVOKED';

export interface SportOption {
  id: string;
  code: string;
  displayName: string;
  active: boolean;
}

export interface LeagueListItem {
  id: string;
  name: string;
  sportId: string;
  sportCode: string;
  sportName: string;
  country: string;
  description?: string | null;
  accessMode: LeagueAccessMode;
  participationType?: LeagueParticipationType;
  status: string;
  teamsCount: number;
  matchesCount: number;
  categoriesCount: number;
  creatorEmail?: string | null;
  updatedAt?: string | null;
  permissions?: ResourcePermissions | null;
}

export interface LeagueSummary extends LeagueListItem {
  allowedCountries?: string[];
  invitedEmails?: string[];
  lateApplicationPolicy?: string | null;
  createdAt?: string | null;
}

export interface LeagueRoleAssignment {
  id: string;
  leagueId: string;
  userSubject?: string | null;
  userEmail?: string | null;
  role: LeagueRole;
  status: LeagueRoleStatus;
  invitedBy?: string | null;
  acceptedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeagueRoleAssignmentPayload {
  userSubject?: string | null;
  userEmail?: string | null;
  role: LeagueRole;
  status?: LeagueRoleStatus;
}

export interface LeagueCategory {
  id: string;
  leagueId: string;
  name: string;
  gender?: string | null;
  birthDateMin?: string | null;
  birthDateMax?: string | null;
  maxTeams?: number | null;
  periods?: number | null;
  periodTime?: number | null;
  description?: string | null;
  minPlayers?: number | null;
  maxPlayers?: number | null;
  sortOrder?: number | null;
}

export interface LeagueParticipant {
  id: string;
  leagueId: string;
  categoryId: string;
  sourceTeamId: string;
  participantName: string;
  clubName?: string | null;
  contactEmail?: string | null;
  country?: string | null;
  state?: string | null;
  city?: string | null;
  gender?: string | null;
  homeColor?: string | null;
  awayColor?: string | null;
  goalkeeperColor?: string | null;
  playerCount: number;
  officialCount: number;
  status: string;
  registeredAt: string;
  permissions?: ResourcePermissions | null;
}

export interface LeagueVenue {
  id: string;
  leagueId: string;
  name: string;
  location?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface LeagueCourt {
  id: string;
  venueId: string;
  name: string;
}

export interface LeagueMatch {
  id: string;
  leagueId: string;
  categoryId: string;
  categoryName?: string | null;
  venueId?: string | null;
  venueName?: string | null;
  courtId?: string | null;
  courtName?: string | null;
  teamAParticipantId: string;
  teamBParticipantId: string;
  teamAId: string;
  teamBId: string;
  teamAName: string;
  teamBName: string;
  label?: string | null;
  matchTime?: string | null;
  scheduleType: LeagueScheduleType;
  recurrenceSeriesId?: string | null;
  status: LeagueMatchStatus;
  teamAScore: number;
  teamBScore: number;
  notes?: string | null;
  createdBy?: string | null;
  permissions?: ResourcePermissions | null;
  sourceType?: 'LEAGUE' | 'EVENT';
  eventId?: string | null;
  eventMatchId?: string | null;
  protocolMatchId?: string | null;
  scoringMode?: LeagueEventMatchScoringMode | null;
}

export interface LeagueStanding {
  teamId: string;
  teamName: string;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
}

export interface LeagueTeamMetric {
  teamId: string;
  teamName: string;
  value: number;
}

export interface LeaguePlayerMetric {
  playerProfileId?: string | null;
  personId?: string | null;
  playerNumber?: string | null;
  displayName: string;
  teamId: string;
  teamName?: string | null;
  goals: number;
  assists: number;
  saves: number;
  misses: number;
  matches: number;
}

export interface LeagueStatistics {
  totalTeams: number;
  totalParticipations: number;
  totalMatches: number;
  finishedMatches: number;
  mostActiveTeam?: LeagueTeamMetric | null;
  bestTeam?: LeagueTeamMetric | null;
  topScorer?: LeaguePlayerMetric | null;
  standings: LeagueStanding[];
  topScorers: LeaguePlayerMetric[];
}

export interface LeagueWorkspace {
  league: LeagueSummary;
  categories: LeagueCategory[];
  participants: LeagueParticipant[];
  venues: LeagueVenue[];
  courtsByVenue: Record<string, LeagueCourt[]>;
  matches: LeagueMatch[];
  statistics: LeagueStatistics;
}

export interface LeagueCreatePayload {
  name: string;
  sportId?: string;
  sportCode?: string;
  country: string;
  description?: string;
  accessMode?: LeagueAccessMode;
  allowedCountries?: string[];
  invitedEmails?: string[];
  participationType?: LeagueParticipationType;
  lateApplicationPolicy?: string;
}

export interface LeagueUpdatePayload extends LeagueCreatePayload {
  status?: string;
}

export interface LeagueCategoryPayload {
  name: string;
  gender?: string;
  birthDateMin?: string;
  birthDateMax?: string;
  maxTeams?: number;
  periods?: number;
  periodTime?: number;
  description?: string;
  minPlayers?: number;
  maxPlayers?: number;
}

export interface LeagueParticipantPayload {
  existingTeamId: string;
  categoryId: string;
  contactEmail?: string;
}

export interface LeagueVenuePayload {
  name: string;
  location?: string;
}

export interface LeagueMatchCreatePayload {
  categoryId: string;
  teamAParticipantId: string;
  teamBParticipantId: string;
  venueId?: string;
  courtId?: string;
  label?: string;
  matchTime?: string;
  scheduleType?: LeagueScheduleType;
  recurrenceWeeks?: number;
  notes?: string;
}

export interface LeagueMatchUpdatePayload {
  categoryId: string;
  teamAParticipantId: string;
  teamBParticipantId: string;
  venueId?: string | null;
  courtId?: string | null;
  label?: string | null;
  matchTime?: string | null;
  scheduleType: LeagueScheduleType;
  status?: LeagueMatchStatus;
  notes?: string | null;
}

export interface LeagueMemberPayload {
  personId?: string;
  playerProfileId?: string;
  categoryId?: string;
  preferredName?: string;
  legalName?: string;
  playerNumber?: string;
  contactEmail?: string;
  phoneNumber?: string;
  birthYear?: number;
  sex?: string;
  memberType?: string;
  gamvaGroup?: string;
  gamvaRating?: number;
  invitedByMemberId?: string;
}

export interface LeagueMember {
  id: string;
  leagueId: string;
  personId?: string | null;
  playerProfileId?: string | null;
  categoryId?: string | null;
  userEmail?: string | null;
  preferredName: string;
  legalName?: string | null;
  playerNumber?: string | null;
  contactEmail?: string | null;
  phoneNumber?: string | null;
  birthYear?: number | null;
  sex?: string | null;
  memberType: string;
  gamvaGroup?: string | null;
  gamvaRating?: number | null;
  invitedByMemberId?: string | null;
  claimStatus: string;
  status: string;
  registeredAt: string;
  claimedAt?: string | null;
  updatedAt: string;
}

export interface LeagueMemberUpdatePayload {
  categoryId?: string;
  preferredName?: string;
  legalName?: string;
  playerNumber?: string;
  contactEmail?: string;
  phoneNumber?: string;
  birthYear?: number;
  sex?: string;
  memberType?: string;
  gamvaGroup?: string;
  gamvaRating?: number;
  status?: string;
}

export interface LeagueEvent {
  id: string;
  leagueId: string;
  name: string;
  eventDate: string;
  registrationOpensAt?: string | null;
  registrationClosesAt?: string | null;
  checkInOpensAt?: string | null;
  startsAt?: string | null;
  status: LeagueEventStatus;
  venueId?: string | null;
  courtId?: string | null;
  capacity?: number | null;
  feeAmount?: number | null;
  currency?: string | null;
  matchTimeMinutes?: number | null;
  periods?: number | null;
  periodTimeMinutes?: number | null;
  breakBetweenPeriodsMinutes?: number | null;
  minPlayersPerTeam?: number | null;
  maxPlayersPerTeam?: number | null;
  defaultScoringMode?: LeagueEventMatchScoringMode | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeagueEventPayload {
  name: string;
  eventDate: string;
  registrationOpensAt?: string;
  registrationClosesAt?: string;
  checkInOpensAt?: string;
  startsAt?: string;
  status?: LeagueEventStatus;
  venueId?: string;
  courtId?: string;
  capacity?: number;
  feeAmount?: number;
  currency?: string;
  matchTimeMinutes?: number;
  periods?: number;
  periodTimeMinutes?: number;
  breakBetweenPeriodsMinutes?: number;
  minPlayersPerTeam?: number;
  maxPlayersPerTeam?: number;
  defaultScoringMode?: LeagueEventMatchScoringMode;
  notes?: string;
}

export interface LeagueEventApplicationPayload {
  leagueMemberId?: string;
  member?: LeagueMemberPayload;
  displayName?: string;
  playerNumber?: string;
  source?: 'NORMAL' | 'QR' | 'ADMIN' | 'WALK_UP';
}

export interface LeagueEventApplication {
  id: string;
  eventId: string;
  leagueId: string;
  leagueMemberId: string;
  personId?: string | null;
  playerProfileId?: string | null;
  source: string;
  status: LeagueEventApplicationStatus;
  displayName: string;
  playerNumber?: string | null;
  contactEmail?: string | null;
  invitedByMemberId?: string | null;
  submittedAt: string;
  updatedAt: string;
}

export interface LeagueEventCheckInPayload {
  applicationId: string;
  present?: boolean;
  punctual?: boolean;
  paymentStatus?: 'UNPAID' | 'PAID' | 'WAIVED' | 'REFUNDED';
  paymentMethod?: 'CASH' | 'CARD' | 'TRANSFER' | 'OTHER';
  amountPaid?: number;
  currency?: string;
  notes?: string;
}

export interface LeagueEventCheckIn {
  id: string;
  eventId: string;
  applicationId: string;
  leagueMemberId: string;
  checkedInAt?: string | null;
  checkedInBy?: string | null;
  present: boolean;
  punctual: boolean;
  paymentStatus: string;
  paymentMethod?: string | null;
  amountPaid?: number | null;
  currency?: string | null;
  paymentValidatedBy?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeagueEventTeamGeneratePayload {
  teamCount?: number;
  playersPerTeam?: number;
  teamNames?: string[];
  colors?: string[];
  divisionRules?: LeagueEventTeamGenerateDivisionRule[];
}

export interface LeagueEventTeamGenerateDivisionRule {
  categoryId?: string | null;
  teamCount?: number;
  playersPerTeam?: number;
  teamNames?: string[];
  colors?: string[];
}

export interface LeagueEventTeamMember {
  id: string;
  eventTeamId: string;
  eventId: string;
  leagueMemberId: string;
  applicationId?: string | null;
  displayName: string;
  playerNumber?: string | null;
  rating?: number | null;
  sortOrder: number;
  createdAt: string;
}

export interface LeagueEventTeamMemberMovePayload {
  targetTeamId: string;
}

export interface LeagueEventTeam {
  id: string;
  eventId: string;
  leagueId: string;
  categoryId?: string | null;
  categoryName?: string | null;
  name: string;
  color?: string | null;
  seedOrder: number;
  members: LeagueEventTeamMember[];
  createdAt: string;
  updatedAt: string;
}

export interface LeagueEventMatchPayload {
  teamAEventTeamId: string;
  teamBEventTeamId: string;
  scoringMode?: LeagueEventMatchScoringMode;
  venueId?: string;
  courtId?: string;
  label?: string;
  matchTime?: string;
  notes?: string;
}

export interface LeagueEventMatchUpdatePayload {
  scoringMode?: LeagueEventMatchScoringMode;
  venueId?: string;
  courtId?: string;
  label?: string;
  matchTime?: string;
  notes?: string;
  status?: LeagueMatchStatus;
}

export interface LeagueEventScheduleGeneratePayload {
  scoringMode?: LeagueEventMatchScoringMode;
}

export interface LeagueEventMatchScorePayload {
  teamAScore?: number;
  teamBScore?: number;
  status?: LeagueMatchStatus;
}

export interface LeagueEventMatch {
  id: string;
  eventId: string;
  leagueId: string;
  teamAEventTeamId: string;
  teamAName?: string | null;
  teamBEventTeamId: string;
  teamBName?: string | null;
  protocolMatchId?: string | null;
  venueId?: string | null;
  courtId?: string | null;
  label?: string | null;
  matchTime?: string | null;
  scoringMode: LeagueEventMatchScoringMode;
  status: LeagueMatchStatus;
  teamAScore: number;
  teamBScore: number;
  notes?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface QrLinkPayload {
  targetType: QrTargetType;
  actionType?: QrActionType;
  code?: string;
  label?: string;
  personId?: string;
  playerProfileId?: string;
  leagueId?: string;
  leagueEventId?: string;
  tournamentId?: string;
  matchId?: string;
  userSubject?: string;
  userEmail?: string;
  metadataJson?: string;
  expiresAt?: string;
}

export interface QrLink {
  id: string;
  code: string;
  targetType: QrTargetType;
  actionType: QrActionType;
  label?: string | null;
  status: string;
  personId?: string | null;
  playerProfileId?: string | null;
  leagueId?: string | null;
  leagueEventId?: string | null;
  tournamentId?: string | null;
  matchId?: string | null;
  userSubject?: string | null;
  userEmail?: string | null;
  metadataJson?: string | null;
  expiresAt?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface QrResolve {
  code: string;
  targetType: QrTargetType;
  actionType: QrActionType;
  label?: string | null;
  status: string;
  personId?: string | null;
  playerProfileId?: string | null;
  playerDisplayName?: string | null;
  leagueId?: string | null;
  leagueName?: string | null;
  leagueEventId?: string | null;
  leagueEventName?: string | null;
  leagueEventDate?: string | null;
  leagueEventStartsAt?: string | null;
  tournamentId?: string | null;
  tournamentName?: string | null;
  matchId?: string | null;
  matchLabel?: string | null;
  matchPublicUrl?: string | null;
  streamingUrl?: string | null;
  userSubject?: string | null;
  userEmail?: string | null;
  metadataJson?: string | null;
  expiresAt?: string | null;
  availableActions?: string[];
}

export interface QrRegistrationRequest {
  member: LeagueMemberPayload;
}

export interface QrRegistration {
  member: LeagueMember;
  application?: LeagueEventApplication | null;
  checkIn?: LeagueEventCheckIn | null;
  qr: QrResolve;
}

export interface QrAcceptLeagueRole {
  assignment: LeagueRoleAssignment;
  qr: QrResolve;
}

async function fetchNextJson<T>(path: string, init: RequestInit, parse: (json: unknown) => T): Promise<T> {
  const apiConfig = await getApiConfigNext();
  const response = await fetch(`${apiConfig.basePath}${path}`, {
    ...init,
    headers: {
      ...(init.headers || {}),
      ...(apiConfig.headers || {}),
    },
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  if (response.status === 204) {
    return parse(null);
  }

  return parse(await response.json());
}

const jsonHeaders = { 'Content-Type': 'application/json' };

export const LeagueDataService = {
  getSports: async (): Promise<SportOption[]> =>
    fetchNextJson('/sports', { method: 'GET' }, (json) => (Array.isArray(json) ? (json as SportOption[]) : [])),

  getAllTeams: async (): Promise<ManagedTeamOption[]> => TeamDataService.getAllTeamsSummary().then((teams) =>
    teams
      .filter((team) => !!team.id)
      .map((team) => ({
        id: team.id!,
        name: team.name || team.id!,
        playersCount: 0,
        officialsCount: 0,
      }))
  ),

  getOwnedTeams: async (): Promise<ManagedTeamOption[]> => TeamDataService.getOwnedTeams(),

  listLeagues: async (): Promise<LeagueListItem[]> =>
    fetchNextJson('/leagues', { method: 'GET' }, (json) => {
      const response = json as { leagues?: LeagueListItem[] };
      return response.leagues ?? [];
    }),

  getLeagueWorkspace: async (leagueId: string): Promise<LeagueWorkspace> =>
    fetchNextJson(`/leagues/${leagueId}`, { method: 'GET' }, (json) => json as LeagueWorkspace),

  createLeague: async (payload: LeagueCreatePayload): Promise<LeagueWorkspace> =>
    fetchNextJson('/leagues', {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueWorkspace),

  updateLeague: async (leagueId: string, payload: LeagueUpdatePayload): Promise<LeagueWorkspace> =>
    fetchNextJson(`/leagues/${leagueId}`, {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueWorkspace),

  deleteLeague: async (leagueId: string): Promise<void> => {
    await fetchNextJson(`/leagues/${leagueId}`, {
      method: 'DELETE',
    }, () => undefined);
  },

  createCategory: async (leagueId: string, payload: LeagueCategoryPayload): Promise<LeagueCategory> =>
    fetchNextJson(`/leagues/${leagueId}/categories`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueCategory),

  updateCategory: async (leagueId: string, categoryId: string, payload: LeagueCategoryPayload): Promise<LeagueCategory> =>
    fetchNextJson(`/leagues/${leagueId}/categories/${categoryId}`, {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueCategory),

  deleteCategory: async (leagueId: string, categoryId: string): Promise<void> => {
    await fetchNextJson(`/leagues/${leagueId}/categories/${categoryId}`, {
      method: 'DELETE',
    }, () => undefined);
  },

  addParticipant: async (leagueId: string, payload: LeagueParticipantPayload): Promise<LeagueParticipant> =>
    fetchNextJson(`/leagues/${leagueId}/participants`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueParticipant),

  createVenue: async (leagueId: string, payload: LeagueVenuePayload): Promise<LeagueVenue> =>
    fetchNextJson(`/leagues/${leagueId}/venues`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueVenue),

  updateVenue: async (leagueId: string, venueId: string, payload: LeagueVenuePayload): Promise<LeagueVenue> =>
    fetchNextJson(`/leagues/${leagueId}/venues/${venueId}`, {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueVenue),

  deleteVenue: async (leagueId: string, venueId: string): Promise<void> => {
    await fetchNextJson(`/leagues/${leagueId}/venues/${venueId}`, {
      method: 'DELETE',
    }, () => undefined);
  },

  createCourt: async (leagueId: string, venueId: string, name: string): Promise<LeagueCourt> =>
    fetchNextJson(`/leagues/${leagueId}/venues/${venueId}/courts`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ name }),
    }, (json) => json as LeagueCourt),

  createMatch: async (leagueId: string, payload: LeagueMatchCreatePayload): Promise<LeagueMatch[]> =>
    fetchNextJson(`/leagues/${leagueId}/matches`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => {
      const response = json as { matches?: LeagueMatch[] };
      return response.matches ?? [];
    }),

  updateMatch: async (leagueId: string, matchId: string, payload: LeagueMatchUpdatePayload): Promise<LeagueMatch> =>
    fetchNextJson(`/leagues/${leagueId}/matches/${matchId}`, {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueMatch),

  deleteMatch: async (leagueId: string, matchId: string): Promise<void> => {
    await fetchNextJson(`/leagues/${leagueId}/matches/${matchId}`, {
      method: 'DELETE',
    }, () => undefined);
  },

  listMembers: async (leagueId: string): Promise<LeagueMember[]> =>
    fetchNextJson(`/leagues/${leagueId}/members`, { method: 'GET' }, (json) => Array.isArray(json) ? (json as LeagueMember[]) : []),

  createMember: async (leagueId: string, payload: LeagueMemberPayload): Promise<LeagueMember> =>
    fetchNextJson(`/leagues/${leagueId}/members`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueMember),

  claimMemberInvitations: async (email: string): Promise<LeagueMember[]> => {
    const normalizedEmail = email.trim().toLowerCase();
    return fetchNextJson(`/users/${encodeURIComponent(normalizedEmail)}/league-member-claims`, {
      method: 'POST',
    }, (json) => Array.isArray(json) ? (json as LeagueMember[]) : []);
  },

  updateMember: async (leagueId: string, memberId: string, payload: LeagueMemberUpdatePayload): Promise<LeagueMember> =>
    fetchNextJson(`/leagues/${leagueId}/members/${memberId}`, {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueMember),

  deleteMember: async (leagueId: string, memberId: string): Promise<void> => {
    await fetchNextJson(`/leagues/${leagueId}/members/${memberId}`, {
      method: 'DELETE',
    }, () => undefined);
  },

  listEvents: async (leagueId: string): Promise<LeagueEvent[]> =>
    fetchNextJson(`/leagues/${leagueId}/events`, { method: 'GET' }, (json) => Array.isArray(json) ? (json as LeagueEvent[]) : []),

  createEvent: async (leagueId: string, payload: LeagueEventPayload): Promise<LeagueEvent> =>
    fetchNextJson(`/leagues/${leagueId}/events`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueEvent),

  updateEvent: async (leagueId: string, eventId: string, payload: LeagueEventPayload): Promise<LeagueEvent> =>
    fetchNextJson(`/leagues/${leagueId}/events/${eventId}`, {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueEvent),

  deleteEvent: async (leagueId: string, eventId: string): Promise<void> => {
    await fetchNextJson(`/leagues/${leagueId}/events/${eventId}`, {
      method: 'DELETE',
    }, () => undefined);
  },

  listApplications: async (leagueId: string, eventId: string): Promise<LeagueEventApplication[]> =>
    fetchNextJson(`/leagues/${leagueId}/events/${eventId}/applications`, { method: 'GET' }, (json) =>
      Array.isArray(json) ? (json as LeagueEventApplication[]) : []),

  applyToEvent: async (
    leagueId: string,
    eventId: string,
    payload: LeagueEventApplicationPayload
  ): Promise<LeagueEventApplication> =>
    fetchNextJson(`/leagues/${leagueId}/events/${eventId}/applications`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueEventApplication),

  updateApplicationStatus: async (
    leagueId: string,
    eventId: string,
    applicationId: string,
    status: LeagueEventApplicationStatus
  ): Promise<LeagueEventApplication> =>
    fetchNextJson(`/leagues/${leagueId}/events/${eventId}/applications/${applicationId}/status`, {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify({ status }),
    }, (json) => json as LeagueEventApplication),

  checkInEventApplication: async (
    leagueId: string,
    eventId: string,
    payload: LeagueEventCheckInPayload
  ): Promise<LeagueEventCheckIn> =>
    fetchNextJson(`/leagues/${leagueId}/events/${eventId}/check-ins`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueEventCheckIn),

  listEventTeams: async (leagueId: string, eventId: string): Promise<LeagueEventTeam[]> =>
    fetchNextJson(`/leagues/${leagueId}/events/${eventId}/teams`, { method: 'GET' }, (json) =>
      Array.isArray(json) ? (json as LeagueEventTeam[]) : []),

  generateEventTeams: async (
    leagueId: string,
    eventId: string,
    payload: LeagueEventTeamGeneratePayload
  ): Promise<LeagueEventTeam[]> =>
    fetchNextJson(`/leagues/${leagueId}/events/${eventId}/teams/generate`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => Array.isArray(json) ? (json as LeagueEventTeam[]) : []),

  resetEventTeams: async (leagueId: string, eventId: string): Promise<void> => {
    await fetchNextJson(`/leagues/${leagueId}/events/${eventId}/teams`, {
      method: 'DELETE',
    }, () => undefined);
  },

  deleteEventTeam: async (leagueId: string, eventId: string, teamId: string): Promise<void> => {
    await fetchNextJson(`/leagues/${leagueId}/events/${eventId}/teams/${teamId}`, {
      method: 'DELETE',
    }, () => undefined);
  },

  moveEventTeamMember: async (
    leagueId: string,
    eventId: string,
    teamMemberId: string,
    payload: LeagueEventTeamMemberMovePayload
  ): Promise<LeagueEventTeam[]> =>
    fetchNextJson(`/leagues/${leagueId}/events/${eventId}/teams/members/${teamMemberId}`, {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => Array.isArray(json) ? (json as LeagueEventTeam[]) : []),

  listEventMatches: async (leagueId: string, eventId: string): Promise<LeagueEventMatch[]> =>
    fetchNextJson(`/leagues/${leagueId}/events/${eventId}/matches`, { method: 'GET' }, (json) =>
      Array.isArray(json) ? (json as LeagueEventMatch[]) : []),

  createEventMatch: async (
    leagueId: string,
    eventId: string,
    payload: LeagueEventMatchPayload
  ): Promise<LeagueEventMatch> =>
    fetchNextJson(`/leagues/${leagueId}/events/${eventId}/matches`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueEventMatch),

  updateEventMatch: async (
    leagueId: string,
    eventId: string,
    matchId: string,
    payload: LeagueEventMatchUpdatePayload
  ): Promise<LeagueEventMatch> =>
    fetchNextJson(`/leagues/${leagueId}/events/${eventId}/matches/${matchId}`, {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueEventMatch),

  generateEventSchedule: async (
    leagueId: string,
    eventId: string,
    payload: LeagueEventScheduleGeneratePayload
  ): Promise<LeagueEventMatch[]> =>
    fetchNextJson(`/leagues/${leagueId}/events/${eventId}/matches/generate-schedule`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => Array.isArray(json) ? (json as LeagueEventMatch[]) : []),

  updateEventMatchScore: async (
    leagueId: string,
    eventId: string,
    matchId: string,
    payload: LeagueEventMatchScorePayload
  ): Promise<LeagueEventMatch> =>
    fetchNextJson(`/leagues/${leagueId}/events/${eventId}/matches/${matchId}/score`, {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueEventMatch),

  deleteEventMatch: async (leagueId: string, eventId: string, matchId: string): Promise<void> => {
    await fetchNextJson(`/leagues/${leagueId}/events/${eventId}/matches/${matchId}`, {
      method: 'DELETE',
    }, () => undefined);
  },

  listRoles: async (leagueId: string): Promise<LeagueRoleAssignment[]> =>
    fetchNextJson(`/leagues/${leagueId}/roles`, { method: 'GET' }, (json) => Array.isArray(json) ? (json as LeagueRoleAssignment[]) : []),

  createRole: async (leagueId: string, payload: LeagueRoleAssignmentPayload): Promise<LeagueRoleAssignment> =>
    fetchNextJson(`/leagues/${leagueId}/roles`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueRoleAssignment),

  updateRole: async (leagueId: string, roleId: string, payload: LeagueRoleAssignmentPayload): Promise<LeagueRoleAssignment> =>
    fetchNextJson(`/leagues/${leagueId}/roles/${roleId}`, {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as LeagueRoleAssignment),

  listQrLinks: async (leagueId: string, leagueEventId?: string): Promise<QrLink[]> => {
    const params = new URLSearchParams({ leagueId });
    if (leagueEventId) {
      params.set('leagueEventId', leagueEventId);
    }
    return fetchNextJson(`/qr-links?${params.toString()}`, { method: 'GET' }, (json) => Array.isArray(json) ? (json as QrLink[]) : []);
  },

  createQrLink: async (payload: QrLinkPayload): Promise<QrLink> =>
    fetchNextJson('/qr-links', {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as QrLink),

  revokeQrLink: async (qrLinkId: string): Promise<QrLink> =>
    fetchNextJson(`/qr-links/${encodeURIComponent(qrLinkId)}/revoke`, {
      method: 'PATCH',
    }, (json) => json as QrLink),

  resolveQr: async (code: string): Promise<QrResolve> =>
    fetchNextJson(`/qr/${encodeURIComponent(code)}`, { method: 'GET' }, (json) => json as QrResolve),

  registerFromQr: async (code: string, payload: QrRegistrationRequest): Promise<QrRegistration> =>
    fetchNextJson(`/qr/${encodeURIComponent(code)}/registrations`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }, (json) => json as QrRegistration),
};
